import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { money } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Download, Mail, BarChart3, Clock, ClipboardCheck } from "lucide-react";

const EXPORTS = [
  ["work-orders", "Work orders"],
  ["inspections", "Inspections"],
  ["invoices", "Invoices"],
  ["issues", "Issues"],
];

const Tile = ({ label, value, sub, testId }) => (
  <div className="rounded-lg border border-border bg-white p-4 sm:p-5" data-testid={testId}>
    <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold break-words">{label}</p>
    <p className="font-display text-2xl sm:text-3xl font-bold text-royal-deep mt-2.5 leading-none">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1.5">{sub}</p>}
  </div>
);

export default function Reports() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setData(null);
    api.get("/analytics", { params: { days } }).then(({ data }) => setData(data));
  }, [days]);

  const download = async (dataset, label) => {
    try {
      const res = await api.get(`/exports/${dataset}.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${dataset}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${label} exported`);
    } catch {
      toast.error(`Could not export ${label.toLowerCase()}`);
    }
  };

  const sendDigests = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/digests/send", {});
      toast.success(data.sent
        ? `Weekly report emailed to ${data.sent} owner(s)`
        : "No owners with properties and a real email yet");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reports");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Proof of work</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">Reports & Analytics</h1>
          <p className="text-muted-foreground mt-3 text-sm md:text-base">
            How your team is performing, how fast issues get fixed, and where the work is going.
          </p>
        </div>
        <Button data-testid="send-digests-btn" disabled={busy} onClick={sendDigests}
          className="w-full sm:w-auto shrink-0 bg-gold hover:bg-gold/90 text-royal-deep font-semibold gap-2 h-11">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
          Send owner reports now
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap" data-testid="range-filters">
        {[30, 90, 365].map((d) => (
          <button key={d} data-testid={`range-${d}`} onClick={() => setDays(d)}
            className={`rounded-full border px-3 sm:px-4 min-h-[42px] text-xs font-medium transition-colors duration-200 ${
              days === d ? "bg-royal text-white border-royal" : "bg-white text-royal border-border hover:border-royal/40"}`}>
            Last {d} days
          </button>
        ))}
      </div>

      {!data ? (
        <Loader2 className="h-6 w-6 animate-spin text-royal" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Tile label="Work orders" value={data.work_orders} sub={`${data.completed} completed`}
                  testId="tile-work-orders" />
            <Tile label="Completion rate" value={`${data.completion_rate}%`} testId="tile-completion" />
            <Tile label="Avg fix time"
                  value={data.avg_resolution_hours !== null ? `${data.avg_resolution_hours}h` : "—"}
                  sub={`${data.issues_resolved} of ${data.issues_reported} issues resolved`}
                  testId="tile-resolution" />
            <Tile label="Condition score" value={data.avg_condition_score ?? "—"}
                  sub={data.inspection_pass_rate !== null ? `${data.inspection_pass_rate}% pass rate` : "No inspections yet"}
                  testId="tile-condition" />
          </div>

          <section>
            <div className="flex items-center gap-3 mb-3">
              <BarChart3 className="h-4 w-4 text-gold" />
              <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">Team performance</h2>
              <div className="h-px flex-1 bg-border" />
            </div>
            {data.team.length === 0 ? (
              <p className="text-sm text-muted-foreground">No work assigned in this period.</p>
            ) : (
              <div className="hidden md:block rounded-lg border border-border bg-white overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/60">
                    <tr className="text-left">
                      {["Person", "Assigned", "Completed", "Completion", "On time", "Cost"].map((h) => (
                        <th key={h} className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.team.map((t) => (
                      <tr key={t.person} data-testid={`team-row-${t.person}`}>
                        <td className="px-4 py-3 font-medium text-royal-deep">{t.person}</td>
                        <td className="px-4 py-3 text-muted-foreground">{t.assigned}</td>
                        <td className="px-4 py-3 text-muted-foreground">{t.completed}</td>
                        <td className="px-4 py-3 text-royal-deep">{t.completion_rate}%</td>
                        <td className={`px-4 py-3 font-semibold ${t.on_time_rate >= 90 ? "text-emerald-700" : "text-amber-700"}`}>
                          {t.on_time_rate}%
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{money(t.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {data.team.length > 0 && (
              <div className="md:hidden space-y-3">
                {data.team.map((t) => (
                  <div key={t.person} className="rounded-lg border border-border bg-white p-4"
                       data-testid={`team-card-${t.person}`}>
                    <p className="text-sm font-semibold text-royal-deep break-words">{t.person}</p>
                    <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
                      {[["Assigned", t.assigned], ["Completed", t.completed],
                        ["Completion", `${t.completion_rate}%`], ["On time", `${t.on_time_rate}%`],
                        ["Cost", money(t.cost)]].map(([k, v]) => (
                        <div key={k}>
                          <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</dt>
                          <dd className="text-sm font-medium text-royal-deep mt-0.5">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="flex items-center gap-3 mb-3">
              <ClipboardCheck className="h-4 w-4 text-gold" />
              <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">Work mix</h2>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {data.by_type.map((t) => (
                <div key={t.work_type} className="rounded-lg border border-border bg-white p-4"
                     data-testid={`type-${t.work_type}`}>
                  <p className="text-sm font-medium text-royal-deep">{t.label}</p>
                  <p className="font-display text-2xl font-bold text-royal mt-1">{t.count}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t.completed} completed · {money(t.cost)}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {data.volume_by_month.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-3">
                <Clock className="h-4 w-4 text-gold" />
                <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">Volume by month</h2>
                <div className="h-px flex-1 bg-border" />
              </div>
              <div className="rounded-lg border border-border bg-white p-5 space-y-2" data-testid="volume-chart">
                {data.volume_by_month.map((m) => {
                  const max = Math.max(...data.volume_by_month.map((x) => x.work_orders)) || 1;
                  return (
                    <div key={m.month} className="flex items-center gap-3">
                      <p className="w-16 text-xs text-muted-foreground">{m.month}</p>
                      <div className="flex-1 h-6 rounded bg-secondary overflow-hidden">
                        <div className="h-full bg-royal transition-all duration-500"
                             style={{ width: `${(m.work_orders / max) * 100}%` }} />
                      </div>
                      <p className="w-8 text-xs font-semibold text-royal-deep text-right">{m.work_orders}</p>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section>
            <div className="flex items-center gap-3 mb-3">
              <Download className="h-4 w-4 text-gold" />
              <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">Exports</h2>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
              {EXPORTS.map(([ds, label]) => (
                <Button key={ds} data-testid={`export-${ds}-btn`} variant="outline"
                  onClick={() => download(ds, label)}
                  className="h-11 gap-2 px-3 text-xs sm:text-sm border-royal/30 text-royal hover:bg-royal/5">
                  <Download className="h-4 w-4" /> {label} CSV
                </Button>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
